import React from "react";
import {
  Box,
  Container,
  Typography,
} from "@mui/material";

const accessibilityAdjustments = [
  "ניווט מלא באתר באמצעות המקלדת בלבד.",
  "מעבר בין כל רכיבי האתר באמצעות מקש Tab.",
  "הפעלת כפתורים, קישורים ושדות באמצעות המקלדת.",
  "סימון ברור של מוקד הפוקוס (Focus).",
  "שימוש בכותרות היררכיות המסייעות לקוראי מסך.",
  "התאמת שדות טפסים, תוויות והודעות שגיאה לטכנולוגיות מסייעות.",
  "הוספת תיאורי תמונה (Alt) לתמונות רלוונטיות.",
  "שמירה על ניגודיות צבעים תקינה.",
  "מבנה תוכן ברור ועקבי.",
  "שימוש ברכיבי HTML תקניים ובמאפייני ARIA.",
  "אפשרות לבצע את כל תהליך הרכישה באמצעות המקלדת בלבד.",
];

const contactRecommendations = [
  "תיאור הבעיה.",
  "כתובת הדף שבו התרחשה הבעיה.",
  "סוג הדפדפן שבו השתמשתם.",
  "סוג המכשיר שממנו גלשתם.",
  "צילום מסך של התקלה, במידת האפשר.",
];

const paragraphStyle = {
  fontSize: {
    xs: "1rem",
    sm: "1.1rem",
    md: "1.125rem",
  },
  lineHeight: {
    xs: 1.8,
    sm: 2,
  },
  mb: {
    xs: 2,
    sm: 3,
  },
  overflowWrap: "anywhere",
};

const sectionTitleStyle = {
  fontWeight: 800,
  fontSize: {
    xs: "1.25rem",
    sm: "1.4rem",
    md: "1.5rem",
  },
  lineHeight: 1.5,
  mb: 2,
};

const informationBoxStyle = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  border: "1px solid #D81B60",
  borderRadius: 2,
  backgroundColor: "#fff8fb",
  boxSizing: "border-box",

  p: {
    xs: 2,
    sm: 3,
  },

  mb: {
    xs: 3,
    sm: 4,
  },
};

export default function AccessibilityStatement() {
  return (
    <Box
      component="main"
      dir="rtl"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        boxSizing: "border-box",

        py: {
          xs: 2,
          sm: 3,
          md: 5,
        },
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",

          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Typography
          component="h1"
          sx={{
            textAlign: "center",
            fontWeight: 800,
            color: "#D81B60",
            lineHeight: 1.3,

            fontSize: {
              xs: "1.75rem",
              sm: "2rem",
              md: "2.25rem",
            },

            mb: {
              xs: 3,
              sm: 4,
            },
          }}
        >
          הצהרת נגישות
        </Typography>

        <Box component="section">
          <Typography
            component="h2"
            sx={sectionTitleStyle}
          >
            הצהרת נגישות – אתר שיינסטוק
          </Typography>

          <Typography
            component="p"
            sx={paragraphStyle}
          >
            אנו בשיינסטוק מחויבים להעניק
            חוויית גלישה נוחה, מכבדת ונגישה
            לכלל המשתמשים, ופועלים באופן
            שוטף לשיפור נגישות האתר עבור
            אנשים עם מוגבלויות ועבור כלל
            לקוחותינו.
          </Typography>

          <Typography
            component="p"
            sx={{
              ...paragraphStyle,
              mb: {
                xs: 3,
                sm: 4,
              },
            }}
          >
            במסגרת תהליך ההנגשה בוצעו באתר
            התאמות רבות בהתאם לעקרונות
            הנגישות המקובלים, במטרה לאפשר
            שימוש נוח, פשוט ונגיש בכל חלקי
            האתר.
          </Typography>
        </Box>

        <Box
          component="section"
          aria-labelledby="accessibility-adjustments-title"
        >
          <Typography
            id="accessibility-adjustments-title"
            component="h2"
            sx={sectionTitleStyle}
          >
            באתר בוצעו בין היתר ההתאמות
            הבאות:
          </Typography>

          <Box sx={informationBoxStyle}>
            <Box
              component="ul"
              sx={{
                m: 0,
                pr: {
                  xs: 2.5,
                  sm: 3,
                },
                pl: 0,
              }}
            >
              {accessibilityAdjustments.map(
                (item) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{
                      mb: 1,
                      pr: 0.5,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: {
                          xs: "0.95rem",
                          sm: "1.05rem",
                          md: "1.125rem",
                        },
                        lineHeight: 1.8,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Box>

        <Box
          component="section"
          aria-labelledby="accessibility-limitations-title"
        >
          <Typography
            id="accessibility-limitations-title"
            component="h2"
            sx={sectionTitleStyle}
          >
            מגבלות נגישות
          </Typography>

          <Typography
            component="p"
            sx={{
              ...paragraphStyle,
              mb: {
                xs: 3,
                sm: 4,
              },
            }}
          >
            למרות המאמצים הרבים להנגיש את
            כלל חלקי האתר, ייתכן שחלקים
            מסוימים עדיין אינם נגישים באופן
            מלא או שטרם נמצא עבורם פתרון
            מיטבי. אנו ממשיכים לפעול באופן
            שוטף לשיפור הנגישות באתר.
          </Typography>
        </Box>

        <Box
          component="section"
          aria-labelledby="accessibility-contact-title"
        >
          <Typography
            id="accessibility-contact-title"
            component="h2"
            sx={sectionTitleStyle}
          >
            יצירת קשר בנושא נגישות
          </Typography>

          <Typography
            component="p"
            sx={paragraphStyle}
          >
            אם נתקלתם בקושי בגלישה באתר או
            שיש לכם הערה, שאלה או הצעה
            בנושא נגישות, נשמח לקבל את
            פנייתכם ולטפל בה בהקדם האפשרי.
          </Typography>

          <Typography
            component="p"
            sx={{
              ...paragraphStyle,
              mb: {
                xs: 3,
                sm: 4,
              },
            }}
          >
            ניתן ליצור קשר באמצעות עמוד
            "צור קשר" באתר או באמצעות פרטי
            ההתקשרות המופיעים באתר.
          </Typography>

          <Box sx={informationBoxStyle}>
            <Typography
              component="h3"
              sx={{
                fontWeight: 700,
                fontSize: {
                  xs: "1.05rem",
                  sm: "1.125rem",
                },
                mb: 1.5,
              }}
            >
              מומלץ לציין בפנייה:
            </Typography>

            <Box
              component="ul"
              sx={{
                m: 0,
                pr: {
                  xs: 2.5,
                  sm: 3,
                },
                pl: 0,
              }}
            >
              {contactRecommendations.map(
                (item) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: {
                          xs: "0.95rem",
                          sm: "1.05rem",
                          md: "1.125rem",
                        },
                        lineHeight: 1.8,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}